import React, { useState, useEffect } from 'react';
import { Menu, User, Moon, Sun, Settings, LogOut, Home, Users, Wrench, DollarSign, ListTodo, ChevronLeft, ChevronRight, Calculator, UserCheck, ClipboardList, Activity } from 'lucide-react';
import { useAppStore } from '../../store';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import GlobalSearch from '../ui/GlobalSearch';
import NotificationIcon from '../ui/NotificationIcon';
import EmployeePointsBadge from '../ui/EmployeePointsBadge';

interface HeaderProps {
  title: string;
  toggleMobileSidebar: () => void;
  toggleSidebar: () => void;
  isSidebarCollapsed: boolean;
}

const capitalizeEs = (s: string) => (s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatLongDateEs = (date: Date): string => {
  const weekday = capitalizeEs(format(date, 'EEEE', { locale: es }));
  const dayNum = format(date, 'd', { locale: es });
  const month = capitalizeEs(format(date, 'MMMM', { locale: es }));
  const year = format(date, 'yyyy', { locale: es });
  return `${weekday}, ${dayNum} de ${month} de ${year}`;
};

const Header: React.FC<HeaderProps> = ({ title, toggleMobileSidebar, toggleSidebar, isSidebarCollapsed }) => {
  const { darkMode, toggleDarkMode } = useAppStore();
  const { user, userRole, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<string>('');
  
  // Get user initials from email
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.substring(0, 2).toUpperCase();
  };
  
  // Get role display name
  const getRoleDisplay = () => {
    return userRole || 'Usuario';
  };

  // Fecha larga + hora con segundos (actualiza cada segundo)
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setCurrentDate(formatLongDateEs(now));
      setCurrentTime(format(now, 'HH:mm:ss'));
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);

    return () => clearInterval(interval);
  }, []);
  
  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              type="button"
              className="text-gray-500 dark:text-gray-400 lg:hidden focus:outline-none"
              onClick={toggleMobileSidebar}
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Desktop sidebar toggle button */}
            <button
              type="button"
              className="hidden lg:block text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none mr-3"
              onClick={toggleSidebar}
              title={isSidebarCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            >
              {isSidebarCollapsed ? <ChevronRight className="h-6 w-6" /> : <ChevronLeft className="h-6 w-6" />}
            </button>
            
            <h1 className="ml-3 lg:ml-0 text-lg font-semibold text-gray-900 dark:text-white">{title}</h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <EmployeePointsBadge />

            {/* Global Search - Only for Admins */}
            <GlobalSearch />
            
            <div className="hidden lg:flex flex-col items-end text-gray-500 dark:text-gray-400">
              <span className="text-sm font-medium text-right leading-tight">{currentDate}</span>
              <span className="text-xs font-bold tabular-nums mt-0.5">{currentTime}</span>
            </div>

            {/* Quick Access Buttons */}
            <div className="flex items-center space-x-1">
              {/* Always show Home */}
                <button 
                  className={`p-2 rounded-lg transition-colors ${
                    location.pathname === '/' 
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => navigate('/')}
                  title="Dashboard"
                >
                  <Home className="h-5 w-5" />
                </button>

                {/* To-Do Diario - Only Admins (second position) */}
                {isAdmin && (
                  <button 
                    className={`p-2 rounded-lg transition-colors ${
                      location.pathname === '/todolist' 
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => navigate('/todolist')}
                    title="Proyectos"
                  >
                    <ListTodo className="h-5 w-5" />
                  </button>
                )}

                {/* Role: Vendedor -> only show Notifications, Theme, Logout */}
              {userRole === 'Vendedor' ? (
                <>
                  {/* Clients for Vendedor */}
                  <button 
                    className={`p-2 rounded-lg transition-colors ${
                      location.pathname === '/clientes' 
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => navigate('/clientes')}
                    title="Clientes"
                  >
                    <Users className="h-5 w-5" />
                  </button>

                  {/* Cotizador for Vendedor */}
                  <button 
                    className={`p-2 rounded-lg transition-colors ${
                      location.pathname === '/cotizador' 
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => navigate('/cotizador')}
                    title="Cotizador"
                  >
                    <Calculator className="h-5 w-5" />
                  </button>

                  {/* CRM Seguimiento for Vendedor */}
                  <button 
                    className={`p-2 rounded-lg transition-colors ${
                      location.pathname === '/seguimiento' 
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => navigate('/seguimiento')}
                    title="Seguimiento CRM"
                  >
                    <UserCheck className="h-5 w-5" />
                  </button>

                  <NotificationIcon />
                  <button 
                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={toggleDarkMode}
                    title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                  >
                    {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>
                  <button 
                    className="p-2 rounded-lg text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={signOut}
                    title="Cerrar sesión"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <>
                  {/* Clients - hidden for Producción */}
                  {userRole !== 'Producción' && (
                    <button 
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/clientes' 
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => navigate('/clientes')}
                      title="Clientes"
                    >
                      <Users className="h-5 w-5" />
                    </button>
                  )}

                  {/* Production Manager */}
                  <button 
                    className={`p-2 rounded-lg transition-colors ${
                      location.pathname === '/productionmanager' 
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => navigate('/productionmanager')}
                    title="Production Manager"
                  >
                    <Wrench className="h-5 w-5" />
                  </button>

                  {/* To-Do Producción for Producción role */}
                  {userRole === 'Producción' && (
                    <button 
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/production-todos' 
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => navigate('/production-todos')}
                      title="To-Do Producción"
                    >
                      <ClipboardList className="h-5 w-5" />
                    </button>
                  )}

                  {/* Expenses - Only for Admins and Production */}
                  {(isAdmin || userRole === 'Producción') && (
                    <button 
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/gastos' 
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => navigate('/gastos')}
                      title="Gastos"
                    >
                      <DollarSign className="h-5 w-5" />
                    </button>
                  )}

                  {/* Notifications */}
                  <NotificationIcon />

                  {/* Dark Mode Toggle */}
                  <button 
                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={toggleDarkMode}
                    title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                  >
                    {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>

                  {/* Activity - Only for Admins */}
                  {isAdmin && (
                    <button 
                      className={`p-2 rounded-lg transition-colors ${
                        location.pathname === '/actividad' 
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => navigate('/actividad')}
                      title="Actividad"
                    >
                      <Activity className="h-5 w-5" />
                    </button>
                  )}

                  {/* Logout */}
                  <button 
                    className="p-2 rounded-lg text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={signOut}
                    title="Cerrar sesión"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            
            <div className="relative">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center text-white">
                  {user ? getUserInitials() : <User className="h-5 w-5" />}
                </div>
                <div className="ml-2 hidden sm:block">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.email ? user.email.split('@')[0] : 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {getRoleDisplay()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;